import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import SiteLayout from '../../components/feature/SiteLayout';
import SectionHeading from '../../components/base/SectionHeading';
import MovieCard from '../../components/base/MovieCard';
import { movies } from '../../mocks/movies';
import { filterMovies, sortMovies } from '../../utils/movies';

const HOT_KEYWORDS = ['科幻', '悬疑', '爱情', '动作', '古装', '动画'];

export default function Search() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get('q') ?? '';
  const [keyword, setKeyword] = useState(query);

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  useEffect(() => {
    document.title = query
      ? `“${query}”的搜索结果 - 中文字幕永久在线`
      : '搜索影视 - 中文字幕永久在线';
  }, [query]);

  const results = useMemo(() => {
    if (!query.trim()) return [];
    const matched = filterMovies(movies, { keyword: query });
    return sortMovies(matched, 'views');
  }, [query]);

  const hotMovies = useMemo(() => sortMovies(movies, 'views').slice(0, 4), []);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    navigate(value ? `/search?q=${encodeURIComponent(value)}` : '/search');
  };

  const clear = () => {
    setKeyword('');
    navigate('/search');
  };

  return (
    <SiteLayout>
      <div className="w-full px-4 md:px-8 py-8 md:py-10 bg-background-50">
        <header className="max-w-3xl mx-auto text-center mb-8">
          <h1 className="font-heading font-extrabold text-2xl md:text-4xl text-foreground-950 mb-4">
            搜索影视作品
          </h1>
          <p className="text-sm md:text-base text-foreground-600 mb-6">
            输入片名、演员、地区或类型，快速找到想看的带中文字幕影视
          </p>
          <form onSubmit={submit} className="flex flex-col sm:flex-row items-stretch gap-2 sm:gap-3">
            <div className="relative flex-1">
              <i
                className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400"
                aria-hidden="true"
              ></i>
              <input
                type="text"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="例如：星海迷航、科幻、韩国"
                className="w-full h-12 pl-11 pr-4 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
              />
              {keyword && (
                <button
                  type="button"
                  onClick={clear}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-full text-foreground-400 hover:text-foreground-700 cursor-pointer"
                  aria-label="清空搜索词"
                >
                  <i className="ri-close-line" aria-hidden="true"></i>
                </button>
              )}
            </div>
            <button
              type="submit"
              className="h-12 px-8 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer"
            >
              搜索
            </button>
          </form>
          <div className="flex flex-wrap items-center justify-center gap-2 mt-4">
            <span className="text-xs text-foreground-400">热门搜索：</span>
            {HOT_KEYWORDS.map((word) => (
              <button
                key={word}
                type="button"
                onClick={() => navigate(`/search?q=${encodeURIComponent(word)}`)}
                className="px-3 py-1 rounded-full bg-background-100 text-foreground-600 text-xs hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
              >
                {word}
              </button>
            ))}
          </div>
        </header>

        {query.trim() ? (
          results.length > 0 ? (
            <section>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
                <p className="text-sm text-foreground-600">
                  搜索 “<span className="text-primary-600 font-medium">{query}</span>” ，共找到{' '}
                  <span className="text-foreground-900 font-semibold">{results.length}</span> 部相关影视
                </p>
                <button
                  type="button"
                  onClick={clear}
                  className="flex items-center gap-1 text-sm text-foreground-500 hover:text-primary-600 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-delete-bin-6-line" aria-hidden="true"></i>
                  清空搜索
                </button>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                {results.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </section>
          ) : (
            <div className="max-w-2xl mx-auto text-center py-14">
              <span className="w-16 h-16 inline-flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
                <i className="ri-emotion-sad-line text-3xl" aria-hidden="true"></i>
              </span>
              <h2 className="font-heading font-semibold text-lg text-foreground-900 mb-2">
                没有找到与 “{query}” 相关的结果
              </h2>
              <p className="text-sm text-foreground-500 mb-6">
                换个关键词试试，或者看看下面的热门影视和分类入口
              </p>
              <div className="flex flex-wrap items-center justify-center gap-3 mb-10">
                {HOT_KEYWORDS.map((word) => (
                  <button
                    key={word}
                    type="button"
                    onClick={() => navigate(`/search?q=${encodeURIComponent(word)}`)}
                    className="px-4 py-2 rounded-md bg-background-100 text-foreground-700 text-sm hover:bg-primary-50 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    {word}
                  </button>
                ))}
              </div>
              <SectionHeading title="大家都在看" moreTo="/category/movie" />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5 text-left">
                {hotMovies.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </div>
          )
        ) : (
          <div className="max-w-4xl mx-auto text-center py-10">
            <span className="w-16 h-16 inline-flex items-center justify-center rounded-full bg-primary-50 text-primary-500 mb-4">
              <i className="ri-search-eye-line text-3xl" aria-hidden="true"></i>
            </span>
            <h2 className="font-heading font-semibold text-lg text-foreground-900 mb-2">开始你的观影搜索</h2>
            <p className="text-sm text-foreground-500 mb-10">输入关键词，发现更多高清中文字幕影视资源</p>
            <SectionHeading title="热门推荐" moreTo="/category/featured" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5 text-left">
              {hotMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-center gap-3 mt-12">
          <Link
            to="/category/movie"
            className="flex items-center gap-2 h-11 px-6 rounded-md bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-600 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
          >
            <i className="ri-movie-2-line" aria-hidden="true"></i>
            浏览高清电影
          </Link>
          <Link
            to="/category/tv"
            className="flex items-center gap-2 h-11 px-6 rounded-md bg-background-100 text-foreground-700 hover:bg-primary-50 hover:text-primary-600 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer"
          >
            <i className="ri-tv-2-line" aria-hidden="true"></i>
            浏览电视剧
          </Link>
        </div>
      </div>
    </SiteLayout>
  );
}